import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import { useAuth } from "../../context/AuthContext";
import { useClickOutside } from "../../hooks/useClickOutside";
import { ROLES, roleLabel } from "../../config/roles";
import { Dropdown } from "../../components/base/Dropdown";
import { Avatar } from "../../components/base/Avatar";
import { formatLongDate } from "../../utils/format";
import { searchService, notificationsService } from "../../services";
import type { NotificationCategory, NotificationItem, SearchResult, SearchResultType } from "../../services/types";

interface TopbarProps {
  onMenuClick: () => void;
  collapsed: boolean;
  onToggleSidebar: () => void;
}

const SEARCH_TYPE_ICON: Record<SearchResultType, string> = {
  member: "ri-user-line",
  group: "ri-group-line",
  receipt: "ri-receipt-line",
  voucher: "ri-file-list-3-line",
};

const SEARCH_TYPE_LABEL: Record<SearchResultType, string> = {
  member: "Member",
  group: "Group",
  receipt: "Receipt",
  voucher: "Payout voucher",
};

const NOTIFICATION_ICON: Record<NotificationCategory, string> = {
  auction_due: "ri-hammer-line",
  auction_overdue: "ri-alarm-warning-line",
  collection_overdue: "ri-wallet-3-line",
  payout_approval: "ri-bank-line",
  demand_overdue: "ri-error-warning-line",
  device_approval: "ri-macbook-line",
};

const NOTIFICATION_TONE: Record<NotificationCategory, string> = {
  auction_due: "bg-info-100 text-info-700",
  auction_overdue: "bg-danger-100 text-danger-600",
  collection_overdue: "bg-warning-100 text-warning-700",
  payout_approval: "bg-primary-100 text-primary-700",
  demand_overdue: "bg-warning-100 text-warning-700",
  device_approval: "bg-primary-100 text-primary-700",
};

/** "5m ago" / "3h ago" / "2d ago" — coarse enough for a notification list, no library needed. */
function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function Topbar({ onMenuClick, collapsed, onToggleSidebar }: TopbarProps) {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchRequestId = useRef(0);

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationBoxRef = useRef<HTMLDivElement>(null);

  useClickOutside(searchBoxRef, () => setSearchOpen(false), searchOpen);
  useClickOutside(notificationBoxRef, () => setNotificationOpen(false), notificationOpen);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [items, count] = await Promise.all([
        notificationsService.listNotifications(),
        notificationsService.getUnreadCount(),
      ]);
      if (!cancelled) {
        setNotifications(items);
        setUnreadCount(count);
      }
    };
    load();
    // Notifications are derived live (no push channel), so a periodic
    // refresh is how a newly-overdue demand or a freshly-submitted payout
    // shows up without a manual page reload.
    const interval = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const query = search.trim();
    if (!query) {
      setResults([]);
      setSearching(false);
      setActiveIndex(-1);
      return;
    }
    setSearching(true);
    const requestId = ++searchRequestId.current;
    const timer = setTimeout(async () => {
      try {
        const found = await searchService.searchAll(query);
        if (searchRequestId.current === requestId) {
          setResults(found);
          setActiveIndex(-1);
        }
      } finally {
        if (searchRequestId.current === requestId) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const goToResult = (result: SearchResult) => {
    navigate(result.link);
    setSearch("");
    setResults([]);
    setSearchOpen(false);
    searchInputRef.current?.blur();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setSearchOpen(false);
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      goToResult(results[activeIndex]);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleSelectResult = (link: string) => {
    setSearch("");
    setShowSearchDropdown(false);
    navigate(link);
  };

  const userMenuItems = [
    { key: "profile", label: "My Profile", icon: "ri-user-line", onClick: () => navigate("/profile") },
    //{ key: "gallery", label: "Component Gallery", icon: "ri-layout-grid-line", onClick: () => navigate("/gallery") },
    { key: "divider", label: "", divider: true },
    { key: "logout", label: "Logout", icon: "ri-logout-box-r-line", danger: true, onClick: handleLogout },
  ];

  const handleNotificationClick = async (n: NotificationItem) => {
    setNotificationOpen(false);
    setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    setUnreadCount((c) => Math.max(0, c - 1));
    await notificationsService.markNotificationRead(n.id);
    navigate(n.link);
  };

  const handleDismissNotification = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    setUnreadCount((c) => Math.max(0, c - 1));
    await notificationsService.markNotificationRead(n.id);
  };

  const handleMarkAllRead = async () => {
    setNotifications([]);
    setUnreadCount(0);
    await notificationsService.markAllNotificationsRead();
  };

  return (
    <header className="no-print sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 backdrop-blur">
      {/* Mobile menu + sidebar toggle */}
      <button type="button" onClick={onMenuClick} aria-label="Open menu" className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 md:hidden">
        <i className="ri-menu-line text-xl" />
      </button>
      <button type="button" onClick={onToggleSidebar} aria-label="Toggle sidebar" className="hidden h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 md:flex">
        <i className={cn("text-xl", collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line")} />
      </button>

      {/* Date */}
      <div className="hidden items-center gap-2 text-sm text-foreground-600 lg:flex">
        <i className="ri-calendar-2-line text-base text-foreground-400" />
        <span className="tnum whitespace-nowrap">{formatLongDate(new Date())}</span>
      </div>

      {/* Search */}
      <div ref={searchBoxRef} className="relative w-full max-w-md">
        <i className="ri-search-line pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-base text-foreground-400" />
        <input
          ref={searchInputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search members, groups, transactions…"
          className="h-9 w-full rounded-md border border-background-200 bg-background-100/50 pl-9 pr-8 text-sm placeholder:text-foreground-400 focus:border-transparent focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
        />
        {searching ? (
          <i className="ri-loader-4-line absolute inset-y-0 right-0 flex w-9 animate-spin items-center justify-center text-base text-foreground-400" />
        ) : search ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setSearch("");
              searchInputRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 flex w-9 cursor-pointer items-center justify-center text-base text-foreground-400 hover:text-foreground-600"
          >
            <i className="ri-close-line" />
          </button>
        ) : null}

        {searchOpen && search.trim() ? (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-md border border-background-200 bg-background-50 py-1 shadow-xl shadow-foreground-950/10">
            {results.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-foreground-500">
                {searching ? "Searching…" : `No results for "${search.trim()}"`}
              </p>
            ) : (
              results.map((r, i) => (
                <button
                  key={`${r.type}-${r.link}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => goToResult(r)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                    i === activeIndex ? "bg-background-100" : "hover:bg-background-100"
                  )}
                >
                  <i className={cn(SEARCH_TYPE_ICON[r.type], "flex-none text-base text-foreground-400")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground-900">{r.label}</span>
                    <span className="block truncate text-xs text-foreground-500">{r.sublabel}</span>
                  </span>
                  <span className="flex-none whitespace-nowrap text-[11px] uppercase tracking-wide text-foreground-400">
                    {SEARCH_TYPE_LABEL[r.type]}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Static Role Badge (Dropdown removed per request) */}
        <div className="hidden h-9 items-center gap-1.5 rounded-md border border-background-200 bg-background-100/60 px-3 text-sm text-foreground-700 sm:flex">
          <span className="whitespace-nowrap text-xs text-foreground-500">Role:</span>
          <span className="whitespace-nowrap font-medium text-foreground-900">{roleLabel(role)}</span>
        </div>

        {/* Notifications */}
        <div ref={notificationBoxRef} className="relative">
          <button
            type="button"
            aria-label="Notifications"
            onClick={() => setNotificationOpen((v) => !v)}
            className={cn(
              "relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100",
              notificationOpen && "bg-background-100"
            )}
          >
            <i className="ri-notification-3-line text-xl" />
            {unreadCount > 0 ? (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-semibold text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            ) : null}
          </button>

          {notificationOpen ? (
            <div className="absolute right-0 top-full z-50 mt-1 w-80 max-w-xs overflow-hidden rounded-md border border-background-200 bg-background-50 shadow-xl shadow-foreground-950/10 sm:max-w-md">
              <div className="flex items-center justify-between border-b border-background-100 px-3 py-2">
                <span className="text-xs font-semibold uppercase text-foreground-500">Notifications</span>
                {unreadCount > 0 ? (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    className="cursor-pointer text-xs font-medium text-primary-600 hover:underline"
                  >
                    Mark all read
                  </button>
                ) : null}
              </div>
              <div className="max-h-96 overflow-y-auto py-1">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
                    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-success-50 text-success-600">
                      <i className="ri-check-line text-lg" />
                    </div>
                    <p className="text-xs font-medium text-foreground-700">You're all caught up</p>
                    <p className="mt-0.5 text-[11px] text-foreground-400">No active notifications</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className="group flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                    >
                      <span className={cn("mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-md", NOTIFICATION_TONE[n.category])}>
                        <i className={cn(NOTIFICATION_ICON[n.category], "text-sm")} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-1.5">
                          <span className="truncate text-xs font-semibold text-foreground-900">
                            {n.title}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => handleDismissNotification(e, n)}
                            title="Dismiss notification"
                            className="rounded p-0.5 text-foreground-400 opacity-0 transition-opacity hover:text-foreground-700 group-hover:opacity-100"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-500">{n.description}</span>
                        <span className="mt-1 block text-[11px] text-foreground-400">{timeAgo(n.createdAt)}</span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* User menu */}
        <Dropdown
          align="right"
          width="w-60"
          trigger={(open) => (
            <button
              type="button"
              className={cn("flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-background-100", open && "bg-background-100")}
            >
              <Avatar name={user?.name ?? "User"} color={user?.avatarColor} size="sm" />
              <span className="hidden text-left lg:block">
                <span className="block text-sm font-medium leading-tight text-foreground-900">{user?.name}</span>
                <span className="block text-xs leading-tight text-foreground-500">{roleLabel(role)}</span>
              </span>
              <i className="ri-arrow-down-s-line hidden text-base text-foreground-400 lg:block" />
            </button>
          )}
          items={userMenuItems}
        />
      </div>
    </header>
  );
}